import React, { useContext, useEffect, useState } from "react";
import OwTitle from "../../shared/OwTitle/OwTitle";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IStripeOnGetSubscriptionDetailsDTO from "../../../models/DTO/IStripeOnGetSubscriptionDetailsDTO";
import IStripeGetSubscriptionDetailsDTO from "../../../models/DTO/IStripeGetSubscriptionDetailsDTO";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import OwLabel from "../../shared/OwLabel/OwLabel";
import * as _ from "lodash-es";
import UserService from "../../../services/UserService";
import { NumericFormat } from "react-number-format";
import OwPill from "../../shared/OwPill/OwPill";
import { CreditCardBrandIcon } from "../../shared/CreditCardBrandIcon/CreditCardBrandIcon";
import OwButton from "../../shared/OwButton/OwButton";
import IStripeOnCreateCustomerPortalSessionDTO from "../../../models/DTO/IStripeOnCreateCustomerPortalSessionDTO";
import IStripeCreateCustomerPortalSessionDTO from "../../../models/DTO/IStripeCreateCustomerPortalSessionDTO";
import UiContext from "../../../contexts/UiContext";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import { SocketContext } from "../../../contexts/SocketContext";
import UIService from "../../../services/UIService";
import { StripeUpdateCardModal } from "../StripeUpdateCardModal/StripeUpdateCardModal";
import IStripeOnCompleteSubscriptionSetupDTO from "../../../models/DTO/IStripeOnCompleteSubscriptionSetupDTO";
import IStripeCompleteSubscriptionSetupDTO from "../../../models/DTO/IStripeCompleteSubscriptionSetupDTO";
import queryString from "query-string";

type Props = {};

export const Billing = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);
    const uiContext = useContext(UiContext);
    const socket = useContext(SocketContext)!;

    const [subscription, setSubscription] = useState<any | undefined>();
    const [lastTwelveInvoices, setLastTwelveInvoices] = useState<any | undefined>();
    const [paymentMethod, setPaymentMethod] = useState<any | undefined>();
    const [hasLoaded, setHasLoaded] = useState(false);
    const [showUpdateCard, setShowUpdateCard] = useState(false);
    // Set once a payment method was saved from this page, so the refresh runs even when the supplier model
    // in context predates the subscription the API just linked or created.
    const [hasSubscriptionNow, setHasSubscriptionNow] = useState(false);

    const onGetSubscriptionDetails = (data: IStripeOnGetSubscriptionDetailsDTO) => {
        setSubscription(data.subscription);
        // @ts-expect-error
        setLastTwelveInvoices(data.last_twelve_invoices);

        if (data.defaultPaymentMethod.brand || data.defaultPaymentMethod.last4) {
            setPaymentMethod(data.defaultPaymentMethod);
        } else {
            setPaymentMethod(undefined);
        }

        setHasLoaded(true);
        uiContext.setActivityIndicator(false);
    };

    const onCreateCustomerPortalSession = (data: IStripeOnCreateCustomerPortalSessionDTO) => {
        window.location.href = data.session_url;
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.StripeOnGetSubscriptionDetails, fn: onGetSubscriptionDetails },
        { endpoint: EndpointsEnum.StripeOnCreateCustomerPortalSession, fn: onCreateCustomerPortalSession },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        uiContext.setActivityIndicator(true);

        getSubscriptionDetails();

        // Stripe sends the browser back here after a redirect-based confirmation from the update-card modal;
        // finish that setup so the new method is attached (the modal itself is gone after the redirect).
        const query = queryString.parse(window.location.search);

        if (query.redirect_status === "succeeded" && typeof query.setup_intent === "string") {
            const payload: IStripeCompleteSubscriptionSetupDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                setupIntentId: query.setup_intent,
            };

            socket.emit(EndpointsEnum.StripeCompleteSubscriptionSetup, payload);
        }

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const getSubscriptionDetails = (force: boolean = false) => {
        if (supplierModel.stripe_subscription_id || force) {
            const payload: IStripeGetSubscriptionDetailsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
            };

            socket.emit(EndpointsEnum.StripeGetSubscriptionDetails, payload);
        } else {
            setHasLoaded(true);
            uiContext.setActivityIndicator(false);
        }
    };

    const getStatusBadge = (status: string) => {
        return <OwPill title={status} className={"w-5r " + (status.toLowerCase() === "paid" ? "bg-success" : "")} />;
    };

    let subStatusClass = "d-inline-flex rounded-5 fw-bold px-4 py-1 text-white ";

    if (subscription?.status !== "active") {
        subStatusClass += "bg-danger";
    } else {
        subStatusClass += "bg-success";
    }

    const MonthNumber = Intl.NumberFormat("en-US", { minimumIntegerDigits: 2 });

    const createCustomerPortalSession = () => {
        const payload: IStripeCreateCustomerPortalSessionDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.StripeCreateCustomerPortalSession, payload);
    };

    const onPaymentMethodUpdated = (data: IStripeOnCompleteSubscriptionSetupDTO) => {
        if (data.subscription?.id) {
            setHasSubscriptionNow(true);
        }

        uiContext.setActivityIndicator(true);
        getSubscriptionDetails(hasSubscriptionNow || !!data.subscription?.id);
    };

    const updateCardButton = (
        <OwButton
            leftIconClass={"fad fa-credit-card"}
            title={"update_card"}
            fn={() => setShowUpdateCard(true)}
            testId={"billing-update-card"}
        />
    );

    const cardClass = "card " + (UIService.isMainViewLight() ? "" : "bg-dark text-white");

    return (
        <div className={"Billing d-flex flex-column flex-grow-1 h-100"}>
            <OwTitle className={"mt-4"} title={"billing"} leftIconName={"fad fa-dollar-circle"} />
            {showUpdateCard ? (
                <StripeUpdateCardModal
                    socket={socket}
                    notifyFn={uiContext.notifyFn}
                    userModel={userModel}
                    supplierModel={supplierModel}
                    closeModalFn={() => setShowUpdateCard(false)}
                    onUpdatedFn={onPaymentMethodUpdated}
                />
            ) : null}
            {hasLoaded && !subscription ? (
                <div className={"d-flex flex-column align-items-center justify-content-center w-100 h-100 flex-grow-1"}>
                    <OwTitle tag={"h5"} leftIconName={"fad fa-exclamation-triangle"} title={"subscription_oops_contact_support"} />
                    <div className={"mt-3"}>{updateCardButton}</div>
                </div>
            ) : null}
            {subscription ? (
                <div className="mt-4">
                    <div className={cardClass}>
                        <div className="card-header">
                            <div className={"d-flex align-items-center"}>
                                <OwTitle title={"subscription"} tag={"h5"} />
                                <div className={"ms-2"}>{updateCardButton}</div>
                                <div className={"ms-2"}>
                                    <OwButton leftIconClass={"fad fa-pencil"} title={"update_billing"} fn={createCustomerPortalSession} />
                                </div>
                            </div>
                        </div>
                        <div className="card-body">
                            <div className={"d-flex align-items-center"}>
                                <div>
                                    <div className={subStatusClass}>{_.startCase(subscription.status)}</div>
                                </div>
                                <div className={"ms-3"}>
                                    <OwLabel title={"current_period"} isBold={true} postText={":"} className={"me-2"} />
                                    {UserService.parseUnixTimestamp(subscription.current_period_start)} -{" "}
                                    {UserService.parseUnixTimestamp(subscription.current_period_end)}
                                </div>
                            </div>

                            {paymentMethod ? (
                                <div className={"mt-5"}>
                                    <div>
                                        <OwTitle title={"payment_method"} tag={"h5"} />
                                    </div>
                                    <div className={"d-flex"} data-testid={"billing-payment-method"}>
                                        {paymentMethod.brand ? (
                                            <div className={"me-4"}>
                                                <CreditCardBrandIcon brand={paymentMethod.brand} />
                                            </div>
                                        ) : null}
                                        {paymentMethod.type === "us_bank_account" ? (
                                            <div className={"me-4"}>
                                                <i className={"fad fa-university me-2"} />
                                                {paymentMethod.bank_name || <OwLabel title={"bank_account"} />}
                                            </div>
                                        ) : null}
                                        {paymentMethod.last4 ? (
                                            <div className={"me-4"}>
                                                <i className={"fas fa-circle-small fa-2xs"} />{" "}
                                                <i className={"fas fa-circle-small fa-2xs"} />{" "}
                                                <i className={"fas fa-circle-small fa-2xs"} />{" "}
                                                <i className={"fas fa-circle-small fa-2xs"} /> {paymentMethod.last4}
                                            </div>
                                        ) : null}
                                        {paymentMethod.exp_year ? (
                                            <div className={"me-4"}>
                                                <OwLabel title={"exp_date"} postText={":"} className={"me-2"} />
                                                {MonthNumber.format(paymentMethod.exp_month)}/{paymentMethod.exp_year}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div className={"mt-4"}>
                        <div className={cardClass}>
                            <div className="card-header">
                                <div>
                                    <OwTitle title={"invoice_history"} tag={"h5"} />
                                </div>
                            </div>
                            <div className="card-body">
                                <div>
                                    {lastTwelveInvoices?.map((invoice: any, index) => {
                                        return (
                                            <div key={index} className={"d-flex mb-3"}>
                                                <div className={"w-10r"}>
                                                    {UserService.parseUnixTimestamp(invoice.created)}{" "}
                                                    <a href={invoice.hosted_invoice_url} target={"_blank"} rel="noreferrer">
                                                        <i className={"fa-duotone fa-arrow-up-right-from-square"} />
                                                    </a>
                                                </div>
                                                {/*<div className={"w-15r"}>{UserService.parseUnixTimestamp(invoice.period_start)} - {UserService.parseUnixTimestamp(invoice.period_end)}</div>*/}
                                                <div className={"w-10r"}>
                                                    <NumericFormat
                                                        displayType={"text"}
                                                        prefix={"$"}
                                                        fixedDecimalScale={true}
                                                        decimalScale={2}
                                                        value={invoice.amount / 100}
                                                    />
                                                </div>
                                                <div className={"w-10r text-small"}>{getStatusBadge(invoice.status)}</div>
                                                <div className={"w-10r"}>
                                                    <a href={invoice.invoice_pdf} target={"_blank"} rel="noreferrer">
                                                        <i className={"fa-duotone fa-download"} /> PDF
                                                    </a>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};
